import { useContext, useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import { deepObjectMerge, POST_MESSAGE_IN_TYPES, POST_MESSAGE_OUT_TYPES } from "../../helpers/FeatureControlsHelper";
import "./FeaturePreview.scss";
import HeadedPanel from "../HeadedPanel/HeadedPanel";
import { IconJsxer } from "../../helpers/IconHelper";
import FeatureControls from "../FeatureControls/FeatureControls";
import { CUSTOMISED_ASSETS_BASE_URL, CUSTOMISED_URL_MAP, getValFromObject } from "../../config/FeatureDashboardVarMap";
import { createArcadeEntriesRoute, filesRoute, getCampaignThemesRoute, getQuizzesByIdsRoute } from "../../api/routes";
import { assets } from "../../assets/assets";
import { getFeatureAssetBaseUrl } from "../../helpers/FeatureAssets";
import { deriveChainId, findFeature, findLinkedFeatureTriggers, chainContainsFeature } from "../../helpers/CampaignsHelper";
import { CampaignContext } from "../../contexts/CampaignContext";
import axios from "../../api/axios";
import { injectQuizImageSrcUrls } from "../../helpers/QuizHelper";
import { AuthContext } from "../../contexts/AuthContext";
import { ACCOUNT_TYPES } from "../../helpers/AccountTypes";
import CollapsibleHeadedSection from "../CollapsibleHeadedSection/CollapsibleHeadedSection";
import LeftColorBlockBox, { LEFT_COLOR_BLOCK_STYLES } from "../LeftColorBlockBox/LeftColorBlockBox";
import Toggle from "../forms/Toggle/Toggle";
import { CHAINS_HELPER } from "../../helpers/ChainsHelper";
import HMSTimeField from "../forms/HMSTimeField/HMSTimeField";
import config from "../../config";
import { PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS } from "../../helpers/Colours";

const DEV_MODE = false;
const PREVIEW_DEV_URL = "http://localhost:3001";
const isDevMode = () => {
    // console.log('is dev mode? ', process.env.NODE_ENV, DEV_MODE);
    return (process.env.NODE_ENV === "local" || process.env.NODE_ENV === "development") && DEV_MODE;
}

export const currentPassedInPreviewData = {data: {}};
export const postMessageToPreviewFrame = (type, detail, frameId = null) => {
    const frame = frameId || "previewFrame";
    const frameRef = document.getElementById(frame);
    const dataToPost = JSON.parse(JSON.stringify(detail)); // {...detail, ...currentPassedInPreviewData.data};
    deepObjectMerge(dataToPost, currentPassedInPreviewData.data);
    console.log('Post to preview frame: ', frame, frameRef, type, dataToPost);
    frameRef?.contentWindow.postMessage({ type: type, detail: dataToPost }, '*');
}

const previewEdgeBuffer = 5;
const previewWidth = 475;
const previewHeight = 350;

// const quizData = null;


const FeaturePreview = (props) => {
    const frontendBaseUrl = process.env.REACT_APP_AXIOS_ORIGIN;
    const backendBaseUrl = process.env.REACT_APP_API_BASE_URL.split('/api')[0];

    const [campaignContext, setCampaignContext] = useContext(CampaignContext);
    const [authContext, setAuthContext] = useContext(AuthContext);

    const customFonts = campaignContext.campaignData.customFonts;
    const framesLoaded = useRef([false]);
    // Preview frame is a special frame used to display live changes while customising. All other frames used the saved data.
    const frameIds = useRef(['previewFrame']);
    const fontsToLoad = useRef([customFonts?.length ?? 0]);
    const [cancelledFrames, setCancelledFrames] = useState([]);
    const cancelFrame = (id) => {
        setCancelledFrames((oldVal) => [...oldVal, id]);
        framesLoaded.current[frameIds.current.indexOf(id)] = true;
    }

    const [quizData, setQuizData] = useState(null);

    const previewControlsId_ref = useRef('previewFrame');
    const [previewControlsId, setPreviewControlsId] = useState(previewControlsId_ref.current);
    const [feature, setFeature] = useState(props.currentFeature);
    const [featureId, setFeatureId] = useState(props.currentFeature?._id);
    const [streamVars, setStreamVars] = useState(props.featureVars.current);
    const [customisationVars, setCustomisationVars] = useState(props.customisationVars);
    const [controlsLabel, setControlsLabel] = useState(props.currentFeature?.contentLabel);

    // Iteratively remove any props that start with "columnBreak", "subTitle", "new_var"
    const removeUnneededProps = (data) => {
        for (let prop in data) {
            if (prop.startsWith('columnBreak') || prop.startsWith('subTitle') || prop.startsWith('new_var') || prop.startsWith('new_group')) {
                delete data[prop];
            }
        }
        if (data && Object.keys(data)?.length > 0) {
            for (let prop in data) {
                if (typeof data[prop] === 'object') {
                    removeUnneededProps(data[prop]);
                }
            }
        }
    }

    const logoutData = (id) => {
        id = id === 'previewFrame' ? props.currentFeature._id : id;
        console.log('>>>>> Feature ID: ', id);
        console.log('>>>>> Feature Data: ', findFeature(id, campaignContext.campaignData));
        const customisationVars = props.customisationVars;
        if (customisationVars.current.hasOwnProperty(id)) {
            const filteredCustomisationVars = JSON.parse(JSON.stringify(customisationVars.current[id]));
            removeUnneededProps(filteredCustomisationVars);

            console.log('>>>>> Customisation Data: ', filteredCustomisationVars);
        }
        if (customisationVars.default && customisationVars.default.hasOwnProperty(id)) {
            const filteredDefaultCustomisationVars = JSON.parse(JSON.stringify(customisationVars.default[id]));
            removeUnneededProps(filteredDefaultCustomisationVars);

            console.log('>>>>> Default Customisation Data: ', filteredDefaultCustomisationVars);
        }
        const featureVars = props.featureVars;
        if (featureVars.current.hasOwnProperty(id)) {
            console.log('>>>>> Feature vars: ', featureVars.current[id]);
        }
        if (featureVars.default && featureVars.default.hasOwnProperty(id)) {
            console.log('>>>>> Default Feature vars: ', featureVars.default[id]);
        }
    }
    const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
    const forceUpdate = () => {
        setForceUpdateCounter((oldVal) => oldVal + 1);
    }

    const linkedFeatureData = useRef({});
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const [canBeRefreshed, setCanBeRefreshed] = useState(false);
    const [needsRefesh, setNeedsRefresh] = useState(false);
    const [randomPreviewBG, setRandomPreviewBG] = useState(Math.random());

    const [passedInPreviewData, setPassedInPreviewData] = useState({});
    const allFeaturesPassedInPreviewData_ref = useRef({});
    const [allFeaturesPassedInPreviewData, setAllFeaturesPassedInPreviewData] = useState({});
    useEffect(() => {
        currentPassedInPreviewData.data = passedInPreviewData;
    }, [passedInPreviewData]);


    const [maximized, setMaximized] = useState(props.startMaximized || false);
    const [previewX, setPreviewX] = useState(props.startPosX === 'left' ? previewEdgeBuffer : window.innerWidth - previewEdgeBuffer - previewWidth);
    const [previewY, setPreviewY] = useState(props.startPosY === 'bottom' ?  window.innerHeight - previewEdgeBuffer - previewHeight : previewEdgeBuffer);
    const previewPosRef = useRef({ x: props.startPosX === 'left' ? previewEdgeBuffer : window.innerWidth - previewEdgeBuffer - previewWidth, y: props.startPosY === 'bottom' ?  window.innerHeight - previewEdgeBuffer - previewHeight : previewEdgeBuffer })

    const mousePosStartDrag = useRef(null);
    const dragStartHandler = (e) => {
        console.log('Drag start: ', e, e.pageX, e.pageY);
        mousePosStartDrag.current = { x: e.pageX, y: e.pageY };
        e.dataTransfer.dropEffect = "move";
        console.log('Drop effect: ', e.dataTransfer.dropEffect);
        document.body.addEventListener('dragover', dragOverHandler);
        document.body.addEventListener('dragenter', dragOverHandler);
        document.body.addEventListener('dragend', dragEndHandler);
    }
    const dragEndHandler = (e) => {
        e.preventDefault();
        console.log('Drag end: ', e, e.pageX, e.pageY);
        const moveDistX = e.pageX - mousePosStartDrag.current.x;
        const moveDistY = e.pageY - mousePosStartDrag.current.y;
        let newPreviewX = previewPosRef.current.x + moveDistX;
        let newPreviewY = previewPosRef.current.y + moveDistY;
        newPreviewX = newPreviewX >= previewEdgeBuffer ? (newPreviewX <= window.innerWidth - previewEdgeBuffer - previewWidth ? newPreviewX : window.innerWidth - previewEdgeBuffer - previewWidth) : previewEdgeBuffer;
        newPreviewY = newPreviewY >= previewEdgeBuffer ? (newPreviewY <= window.innerHeight - previewEdgeBuffer - previewHeight ? newPreviewY : window.innerHeight - previewEdgeBuffer - previewHeight) : previewEdgeBuffer;

        previewPosRef.current = { x: newPreviewX, y: newPreviewY };
        setPreviewX(newPreviewX);
        setPreviewY(newPreviewY);

        document.body.removeEventListener('dragover', dragOverHandler);
        document.body.removeEventListener('dragenter', dragOverHandler);
        document.body.removeEventListener('dragend', dragEndHandler);
    }
    const dragOverHandler = (e) => {
        e.preventDefault();
        // console.log('Body drag event:', e);
        e.dataTransfer.effectAllowed = "move";
        // console.log('Effect allowed: ', e.dataTransfer.effectAllowed, e.dataTransfer.dropEffect)
    }

    const findFeatureData = (featureId) => {
        const campaignData = campaignContext.campaignData;
        for (let i = 0; i < campaignData.features.length; i++) {
            if (campaignData.features[i]._id === featureId) {
                return campaignData.features[i];
            }
        }
        return null;
    }

    /* THEMES */
    const [isThemeable, setIsThemeable] = useState(false);
    const [themeData, setThemeData] = useState(null);
    const themeDataRef = useRef(null);
    const themeDataFetchedRef = useRef(false);

    const getThemeData = () => {
        // Check tabs in featureData for any themeable tabs
        const featureData = props.currentFeature;
        let themeable = false;
        for (let i = 0; i < featureData.feature.customisation.length; i++) {
            if (featureData.feature.customisation[i].themeable) {
                themeable = true;
                break;
            }
        }

        if (themeable) {
            axios
                .get(getCampaignThemesRoute, { params: { campaignID: props.campaignId }, withCredentials: true })
                .then((res) => {
                    console.log('[THEMES] --- Got theme data: ', res.data, campaignContext.campaignData);
                    themeDataRef.current = res.data.themes;
                    setThemeData(res.data.themes);

                    const fixThemeSrcUrls = (themeData) => {
                        const featureData = props.currentFeature;
                        if (featureData) {
                            const customisationData = featureData.feature.customisation;
                            // console.log('Fix theme srcUrls: ', featureData, themeData);
                            // console.log('Customisation data: ', customisationData);
                            // console.log('-----');
                            for (let tI = 0; tI < themeData.length; tI++) {
                                // console.log('Checking theme data: ', themeData[tI], props.currentFeature._id);
                                setIsThemeable(true);
                                for (let i = 0; i < customisationData.length; i++) {
                                    // const rootVal = customisationVars.current[key];
                                    const tabKey = customisationData[i].tabKey;
                                    for (let j = 0; j < customisationData[i].options.length; j++) {
                                        const varKey = customisationData[i].options[j].varKey;
                                        for (let k = 0; k < customisationData[i].options[j].variables.length; k++) {
                                            const varName = customisationData[i].options[j].variables[k].varName;
                                            // console.log('Valschema: ', customisationData[i].options[j].variables[k].valSchema);
                                            for (let prop in customisationData[i].options[j].variables[k].valSchema) {
                                                const type = customisationData[i].options[j].variables[k].valSchema[prop].type.toLowerCase();
                                                // console.log('Checking for type: ', type);
                                                if (CUSTOMISED_URL_MAP.hasOwnProperty(type)) {
                                                    // We found one we need to map to a url!
                                                    const vals = themeData[tI]?.themeData?.[tabKey]?.[varKey]?.[varName]; // getValFromObject(rootVal[tabKey], varKey, varName);
                                                    // console.log('Fixing urls for: ', vals, themeData[tI], tabKey, varKey, varName);
                                                    if (vals && vals?.length > 0) {
                                                        // console.log('Fixing urls for: ', prop, vals);
                                                        for (let l = 0; l < vals.length; l++) {
                                                            // Reformat legacy string based filenames to be objects...
                                                            // SrcUrl has to be part of the object, meaning all feature customisations are going to break...
                                                            let filename = vals[l][prop];
                                                            if (typeof filename === 'string' || typeof filename === 'undefined' || filename === null) {
                                                                filename = { filename: vals[l][prop] };
                                                                vals[l][prop] = filename;
                                                                filename.isDefault = vals[l].isDefault;
                                                            }

                                                            const srcUrl = filename.srcUrl ||
                                                                getFeatureAssetBaseUrl(false) + 'campaigns/' + (props.campaignId || campaignContext.campaignData._id) + '/' + (themeData[tI].featureId)
                                                                + '/' + CUSTOMISED_URL_MAP[type].dir + '/' + filename.filename;

                                                            // console.log('LOAD ASSET:', srcUrl)

                                                            filename.srcUrl = srcUrl;
                                                            filename.originalFile = filename.filename;
                                                            filename.type = CUSTOMISED_URL_MAP[type].dir;
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            console.log('Theme data fixed: ', themeData);
                            setThemeData(themeData);
                            themeDataFetchedRef.current = true;
                        } else {
                            console.log('No feature data to fix urls with');
                            setTimeout(() => fixThemeSrcUrls(themeData), 100);
                        }
                    }
                    fixThemeSrcUrls(res.data.themes);
                })
        } else {
            themeDataFetchedRef.current = true;
        }
    }

    const passFeatureCustomisationsToFeature = (frameId = 'previewFrame') => {
        if (!themeDataFetchedRef.current) {
            setTimeout(() => passFeatureCustomisationsToFeature(frameId), 100);
            return;
        }
        const key = frameId === 'previewFrame' ? props.currentFeature._id : frameId;
        const customisationVars = props.customisationVars
        console.log('Preview customisations vars: ', customisationVars);

            if (customisationVars.current.hasOwnProperty(key)) {
                // convert any filenames into urls
                const featureData = frameId === 'previewFrame' ? props.currentFeature : linkedFeatureData.current[key];
                // const rootVal = PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS(customisationVars.current[key], featureData, campaignContext.campaignData);
                // const rootVal = PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS(customisationVars.current[key], featureData, campaignContext.campaignData);
                if (featureData) {
                    const customisationData = featureData.feature.customisation;
                    for (let i = 0; i < customisationData.length; i++) {
                        const rootVal = customisationVars.current[key];
                        const tabKey = customisationData[i].tabKey;
                        for (let j = 0; j < customisationData[i].options.length; j++) {
                            const varKey = customisationData[i].options[j].varKey;
                            for (let k = 0; k < customisationData[i].options[j].variables.length; k++) {
                                const varName = customisationData[i].options[j].variables[k].varName;
                                for (let prop in customisationData[i].options[j].variables[k].valSchema) {
                                    const type = customisationData[i].options[j].variables[k].valSchema[prop].type.toLowerCase();
                                    if (CUSTOMISED_URL_MAP.hasOwnProperty(type)) {
                                        // We found one we need to map to a url!
                                        const vals = getValFromObject(rootVal[tabKey], varKey, varName);
                                        for (let l = 0; l < vals.length; l++) {
                                            // Reformat legacy string based filenames to be objects...
                                            // SrcUrl has to be part of the object, meaning all feature customisations are going to break...
                                            let filename = vals[l][prop];
                                            if (typeof filename === 'string' || typeof filename === 'undefined' || filename === null) {
                                                filename = {filename: vals[l][prop]};
                                                vals[l][prop] = filename;
                                                filename.isDefault = vals[l].isDefault;
                                            }

                                            const srcUrl = filename.srcUrl ||  
                                                            (filename.isDefault ? getFeatureAssetBaseUrl(true) + featureData.feature.defaultAssetFolder : getFeatureAssetBaseUrl(false) + 'campaigns/'+(props.campaignId || campaignContext.campaignData._id)+'/'+(key + (props.streamId && vals[l][prop].isStreamLevel ? ('/' + props.streamId) : ''))) 
                                                            + '/' + CUSTOMISED_URL_MAP[type].dir + '/' + filename.filename;

                                            console.log('LOAD ASSET:', srcUrl)
                                            
                                            filename.srcUrl = srcUrl;
                                            filename.originalFile = filename.filename;
                                            filename.type = CUSTOMISED_URL_MAP[type].dir;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                console.log('Posting customisation data to frame');
                console.log(`${key}: `, customisationVars.current[key]);

                // Modify Howl on this frame to fix issues with blob urls
                const frameRef = document.getElementById(frameId);
                // console.log('---> HOWLER, ' + frameId, frameRef.contentWindow, frameRef.contentWindow.Howl.prototype);
                if (!isDevMode() && frameRef.contentWindow.Howl && frameRef.contentWindow.Howl.prototype && frameRef.contentWindow.Howl.prototype.load && !frameRef.contentWindow.Howl.prototype.originalLoadFunction) {
                    const howlerOriginalLoadFunction = frameRef.contentWindow.Howl.prototype.load;
                    frameRef.contentWindow.Howl.prototype.originalLoadFunction = howlerOriginalLoadFunction;
                    frameRef.contentWindow.Howl.prototype.load = function() {
                        var self = this;
                        // console.log('HOWLER LOAD CALLED FOR: ', self);
                        if (typeof self._src === 'string' && self._src.indexOf('blob:') > 0) {
                            let srcSplit = self._src.split('blob:');
                            self._src = ('blob:' + srcSplit[1]).split('.ogg')[0];
                            self._format = 'ogg';
                        } else
                        if (Array.isArray(self._src)) {
                            if (!Array.isArray(self._format)) {
                                self._format = [];
                            }
                            for (let i = 0; i < self._src.length; i++) {
                                if (typeof self._src[i] === 'string' && self._src[i].indexOf('blob:') > 0) {
                                    let srcSplit = self._src[i].split('blob:');
                                    self._src[i] = ('blob:' + srcSplit[1]).split('.ogg')[0];
                                }
                            }
                            while (self._format.length < self._src.length) {
                                self._format.push('ogg');
                            }
                        }
                        self.originalLoadFunction();
                    }
                }

                // Before we post the data to the frame, add in the domains we are running from so the feature can use it to build api requests and such
                // customisationVars.current[key].domain = window.location.origin;
                // customisationVars.current[key].apiDomain = process.env.REACT_APP_API_BASE_URL;

                // Do we need to apply a theme?
                // Clone the customisation data so we can apply the theme to it
                // Note rootVal is now already cloned because our colour injector does it!
                // Note rootVal is now already cloned because our colour injector does it!
                let themedCustomisationData = JSON.parse(JSON.stringify(customisationVars.current[key]));
                const streamVars = props?.featureVars?.current?.[key];
                if (streamVars) {
                    if (streamVars.theme && streamVars.theme.id && streamVars.theme.id !== '') {
                        const themeData = themeDataRef.current.find((theme) => theme._id === streamVars.theme.id);
                        if (themeData) {
                            const themeVars = themeData.themeData;
                            for (let tabKey in themeVars) {
                                if (themedCustomisationData[tabKey]) {
                                    for (let varKey in themeVars[tabKey]) {
                                        if (themedCustomisationData[tabKey][varKey]) {
                                            for (let varName in themeVars[tabKey][varKey]) {
                                                for (let varI = 0; varI < themeVars[tabKey][varKey][varName].length; varI++) {
                                                    let themedVal = false;
                                                    if (Object.keys(themeVars[tabKey][varKey][varName][varI]).length > 0) {
                                                        // If our theme has more values than the customisation data, we need to add them in
                                                        while (themedCustomisationData[tabKey][varKey][varName].length <= varI) {
                                                            // Apply the default values
                                                            const varSchema = featureData.feature.customisation.find((c) => c.tabKey === tabKey).options.find((o) => o.varKey === varKey).variables.find((v) => v.varName === varName).valSchema;
                                                            const defaultVals = {};
                                                            for (let prop in varSchema) {
                                                                defaultVals[prop] = varSchema[prop].default;
                                                            }
                                                            themedCustomisationData[tabKey][varKey][varName].push(defaultVals);
                                                        }
                                                        // Then apply the theme values
                                                        for (let prop in themeVars[tabKey][varKey][varName][varI]) {
                                                            if (themedCustomisationData[tabKey][varKey][varName]?.[varI]?.[prop]) {
                                                                themedCustomisationData[tabKey][varKey][varName][varI][prop] = themeVars[tabKey][varKey][varName][varI][prop];
                                                            }
                                                            themedVal = true;
                                                        }
                                                        if (themedVal) {
                                                            themedCustomisationData[tabKey][varKey][varName][varI].isDefault = false;
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS(themedCustomisationData, featureData, campaignContext.campaignData);

                console.log('Posting themed customisation data to frame: ', themedCustomisationData);

                postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.CUSTOMISATION_VARS_RECEIVED, themedCustomisationData/*customisationVars.current[key]*/, frameId);
            }
        
    }

    const leaderboardData = useRef(null);
    const checkAllLoaded = () => {
        if (framesLoaded.current.indexOf(false) === -1) {
            // Do we have any fonts left to load?
            let fontsLeftToLoad = false;
            for (let i = 0; i < fontsToLoad.current.length; i++) {
                if (fontsToLoad.current[i] > 0) {
                    fontsLeftToLoad = true;
                    break;
                }
            }
            if (!fontsLeftToLoad) {
                setPreviewLoaded(true);
                setCanBeRefreshed(true);
                
                if (props.onLoad) {
                    props.onLoad();
                }
                if (props.autoStart) {
                    setTimeout(() => {
                        sendPrimaryCommand(null, true);
                    }, 500);
                }
            }
        }
    }

    const postMessageToAllFrames = (type, detail) => {
        console.log('POST TO ALL FRAMES')
        for (let i = 0; i < frameIds.current.length; i++) {
            const frameId = frameIds.current[i];
            const frameRef = document.getElementById(frameId);
            if (frameRef && frameRef.contentWindow) {
                postMessageToPreviewFrame(type, detail, frameId);
            }
        }
    }

    const processPostedMessages = (event) => {
        console.log('Koko Manage Stream received message from preview: ', event.origin, event.data);
        switch (event.data.type) {
            case POST_MESSAGE_IN_TYPES.HANDSHAKE:
                // respond that we are connected and ready!
                event.source.postMessage(
                    { type: POST_MESSAGE_OUT_TYPES.CONNECTED, detail: 'Howdy!' },
                    event.origin
                );
                passFeatureCustomisationsToFeature(event.data.id);
                break;
            case POST_MESSAGE_IN_TYPES.FEATURE_LOADED:
                console.log(event, props.featureVars, props.currentFeature);

                const featureId = event.data.id === 'previewFrame' ? props.currentFeature._id : event.data.id;
                postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.SETTINGS_VARS_RECEIVED, props.featureVars.current[featureId], event.data.id);

                // Does this feature require pixi (and therefore a canvas?)
                // If not remove any canvas elements from the iframe content
                const featureData = linkedFeatureData.current[event.data.id];
                const frameRef = document.getElementById(event.data.id);
                // console.log('Feature loaded (looking for requiresPixi): ', event.data.id, featureData, frameRef);
                if (frameRef && frameRef.contentWindow && featureData && featureData.feature && featureData.feature.requiresPixi === false) {
                    // Time to remove our canvas elements
                    const canvasElements = frameRef.contentWindow.document.getElementsByTagName('canvas');
                    // console.log('Canvas elements found: ', canvasElements);
                    for (let i = 0; i < canvasElements.length; i++) {
                        canvasElements[i].remove();
                    }
                }

                if (needsRefesh) {
                    refreshPreview(true);
                } else {
                    // if (framesLoaded.current.indexOf(false) >= 0) {
                        framesLoaded.current[frameIds.current.indexOf(event.data.id)] = true;
                        forceUpdate();
                    // }
                    /* if (framesLoaded.current.indexOf(false) === -1) {
                        setPreviewLoaded(true);
                        setCanBeRefreshed(true);
                        
                        if (props.onLoad) {
                            props.onLoad();
                        }
                        if (props.autoStart) {
                            setTimeout(() => {
                                sendPrimaryCommand(null, true);
                            }, 500);
                        }
                    } */
                    checkAllLoaded();
                    console.log('Frame loaded: ', framesLoaded);

                    // Inject javascript file from public/inject-js/functions.js
                    const script = frameRef.contentWindow.document.createElement('script');
                    script.src = '/inject-js/functions.js';
                    script.onload = () => {
                        // console.log('Inject JS loaded');
                        if (frameRef.contentWindow.test) {
                            frameRef.contentWindow.test();
                        }
                        // Load custom fonts
                        if (frameRef.contentWindow.loadFont) {
                            const customFontLoaded = (e) => {
                                // console.log('Custom font loaded: ', e);
                                fontsToLoad.current[frameIds.current.indexOf(event.data.id)]--;
                                checkAllLoaded();
                            }
                            const customFontsLocation = getFeatureAssetBaseUrl() + `campaigns/${props.campaignId}/fonts/`;
                            if (customFonts?.length) {
                                for (let i = 0; i < customFonts.length; i++) {
                                    frameRef.contentWindow.loadFont(customFonts[i].fontFamily, customFontsLocation + customFonts[i].fontFilename, customFontLoaded);
                                }
                            }
                            // frameRef.contentWindow.loadFont('ALGER', 'http://localhost:3000/custom-font/ALGER.TTF', customFontLoaded);
                        }
                    }
                    frameRef.contentWindow.document.head.appendChild(script);
                }
                break;
            case POST_MESSAGE_IN_TYPES.FEATURE_STARTED:
                console.log('Feature has started! We might need to emit a socket or something here?');
                // socket.current.emit('featureStarted', { streamID: streamId, featureID: event.data.id, timestamp: timestamp });
                // Let every other feature know that this one has started
                postMessageToAllFrames(POST_MESSAGE_OUT_TYPES.FEATURE_STARTED, { featureId: event.data.id });
                break;
            case POST_MESSAGE_IN_TYPES.FEATURE_ENDED:
                console.log('PREVIEW: Feature has ended! We might need to emit a socket or something here?');
                // socket.current.emit('featureEnded', {streamID: streamId});
                if (event.data.id === 'previewFrame') {
                    if (props.onEnded) {
                        props.onEnded();
                    }
                } else {
                    postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.TRIGGERED_FEATURE_ENDED, {featureId: event.data.id, passedData: event.data.passedData}, 'previewFrame');
                    for (let prop in linkedFeatureData.current) {
                        postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.TRIGGERED_FEATURE_ENDED, {featureId: event.data.id, passedData: event.data.passedData}, prop);
                    }
                }
                // Also let every other feature know this feature has ended
                // Importantly, this is seperate to TRIGGERED_FEATURE_ENDED, which is only for locally triggered features
                postMessageToAllFrames(POST_MESSAGE_OUT_TYPES.FEATURE_ENDED, { featureId: event.data.id });
                chainStep(event.data.id);
                break;
            case POST_MESSAGE_IN_TYPES.CONTINUE_CHAIN:
                console.log('PREVIEW: Continue chain for feature: ', event.data.id);
                // socket.current.emit('continueChain', {streamID: streamId, featureID: event.data.id});
                chainStep(event.data.id);
                break;
            case POST_MESSAGE_IN_TYPES.AUDIO_ENABLED:
                console.log('PREVIEW: We have interacted and therefore enabled audio! '/*, audioEnabled.current*/);
                // audioEnabled.current = true;
                // socket.current.emit('audioEnabled', {streamID: streamId});
                break;

                case POST_MESSAGE_IN_TYPES.TRIGGER_FEATURE:
                    console.log('Feature ' + event.data.id + ' triggering another feature: ', event.data.featureId);
                    if (cancelledFrames.indexOf(event.data.featureId) < 0) {
                        // Find the correct command
                        const triggerFeatureId = event.data.featureId.split(':')[0];
                        let triggerCommandParam = 0;
                        const featureData = linkedFeatureData.current[triggerFeatureId];
                        if (featureData) {
                            console.log('Looking for primary command, feature data: ', featureData);
                            let commandString = '';
                            for (let i = 0; i < featureData.feature.commands.length; i++) {
                                if (featureData.feature.commands[i].isPrimaryTrigger) {
                                    commandString = featureData.feature.commands[i].command;
                                    if (featureData.feature.commands[i].triggerParam) {
                                        triggerCommandParam = event.data.featureId.split(':')[1] || featureData.feature.commands[i].triggerParam.default || 0;
                                    }
                                }
                            }
                            if (event.data.triggerData) {
                                event.data.triggerData.triggeredByDisruptor = true;
                                event.data.triggeringDisruptorId = event.data.id;
                                postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.TRIGGER_DATA_RECEIVED, event.data.triggerData, triggerFeatureId);
                            }
                            if (commandString) {
                                console.log('Emitting feature command direct to iframe: ', commandString);
                                // We are not connected, we can't do this with a socket emit, but still want to emulate the correct functionality.
                                // So directly send the command to the frame...
                                postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: commandString, extraData: {username: 'CampaignStudio', hashtag:'Feature Triggered Command', commandParam: triggerCommandParam, triggeringFeatureId: event.data.id} }, triggerFeatureId);
                            } else {
                                setTimeout(
                                    () => {
                                        postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.TRIGGERED_FEATURE_ENDED, {featureId: event.data.id}, 'previewFrame');
                                        for (let prop in linkedFeatureData.current) {
                                            postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.TRIGGERED_FEATURE_ENDED, {featureId: event.data.id}, prop);
                                        }
                                    },
                                    1000
                                )
                            }
                        }
                    } else {
                        // This frame has been cancelled, just post a feature ended message after a second so we don't get stuck
                        setTimeout(
                            () => {
                                postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.TRIGGERED_FEATURE_ENDED, {featureId: event.data.featureId}, 'previewFrame');
                                for (let prop in linkedFeatureData.current) {
                                    postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.TRIGGERED_FEATURE_ENDED, {featureId: event.data.featureId}, prop);
                                }
                            },
                            1000
                        );
                    }
                    break;
    
                case POST_MESSAGE_IN_TYPES.TRIGGER_FEATURE_END:
                    console.log('Feature ' + event.data.id + ' triggering another feature to end: ', event.data.featureId);
                    // Find the correct command
                    const triggerEndFeatureId = event.data.featureId.split(':')[0];
                    const featureData_end = linkedFeatureData.current[triggerEndFeatureId];
                    if (featureData_end) {
                        console.log('Looking for feature end command, feature data: ', featureData_end);
                        let commandString_end = '';
                        for (let i = 0; i < featureData_end.feature.commands.length; i++) {
                            if (featureData_end.feature.commands[i].isEndTrigger) {
                                commandString_end = featureData_end.feature.commands[i].command;
                            }
                        }
                        if (commandString_end) {
                            console.log('Emitting feature end command direct to iframe: ', commandString_end);
                            // We are not connected, we can't do this with a socket emit, but still want to emulate the correct functionality.
                            // So directly send the command to the frame...
                            postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: commandString_end, extraData: {username: 'CampaignStudio', hashtag:'Feature End Triggered Command', commandParam: 0} }, triggerEndFeatureId);
                        }
                    }
                    break;

                case POST_MESSAGE_IN_TYPES.REQUEST_QUIZ_DATA:
                    console.log('Feature ' + event.data.id + ' requesting quiz data');
                    // Axios call to get quiz data by ids
                    axios.get(getQuizzesByIdsRoute, {params: {ids: event.data.quizIds}}).then(
                        (res) => {
                            // We need to update the quiz data to include the correct image srcUrls
                            for (let i = 0; i < res.data.quizzes.length; i++) {
                                injectQuizImageSrcUrls(res.data.quizzes[i]);
                            }

                            // We only need to concern ourselves with the quiz data for the disruptor we are previewing
                            if (event.data.id === 'previewFrame') {
                                // FeaturePreview.quizData = res.data;
                                setQuizData(res.data);
                            }

                            console.log('POST MESSAGE FOR QUIZ', res.data)
                          
                            //window.postMessage({ type: POST_MESSAGE_OUT_TYPES.QUIZ_DATA, detail: res.data }, '*');
                            postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.QUIZ_DATA, res.data, event.data.id);
                        }
                    ).catch(
                        (err) => {
                            console.log('Error getting quiz data: ', err);
                        }
                    )
                    
                    break;

                case POST_MESSAGE_IN_TYPES.LEADERBOARD_DATA:
                    console.log('Feature ' + event.data.id + ' sent leaderboard data: ', event.data.leaderboardData);
                    leaderboardData.current = event.data.leaderboardData;
                    // Update leaderboards!
                    for (let prop in linkedFeatureData.current) {
                        postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, {command: 'RECEIVE_LEADERBOARD_DATA', extraData: {forcedScore: leaderboardData.current}}, prop);
                    }
                    break;

                // This will need removing, it's only here for testing!
                case POST_MESSAGE_IN_TYPES.REPORT_ARCADE_DATA:
                    console.log('Feature ' + event.data.id + ' sent arcade data: ', event.data.arcadeData);
                    const arcadeData = event.data.arcadeData;

                    // Tag it with our campaign level feature Id and name
                    arcadeData.campaignFeatureId = props.currentFeature._id;
                    arcadeData.campaignFeatureName = props.currentFeature.contentLabel || props.currentFeature.feature.featureName;

                    // Insert stream Id and feature Id
                    const dataToSend = {
                        streamID: 'preview_test', // 12 character string is accepted as ObjectId by mongoose
                        featureID: findFeature(props.currentFeature._id, campaignContext.campaignData).feature._id,
                        entryData: arcadeData,
                    }
                    // Now report it using axios
                    axios.post(createArcadeEntriesRoute, dataToSend).then(
                        (res) => {
                            console.log('Arcade data reported: ', res);
                        }
                    ).catch(
                        (err) => {
                            console.log('Error reporting arcade data: ', err);
                        }
                    )
                    break;

            case POST_MESSAGE_IN_TYPES.SET_STREAM_MARKER:
                console.log('Feature ' + event.data.id + ' set a stream marker: ', event.data.description);
                // setStreamMarker(event.data.confirmationID, event.data.description);
                // In preview mode, we are going to just respond as if we have set the marker, without actually doing it (because we can't).
                const fakeResponseData = { channel: 'test_channel', time_secs: 90 + Math.floor(Math.random() * 5000), description: event.data.description, confirmationID: event.data.confirmationID, markerID: 'fake_id_' + Math.floor(Math.random() * 100000) };
                console.log('Marker confirmation received: ', fakeResponseData);
                postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.STREAM_MARKER_CONFIRMED, { markerData: fakeResponseData });
                break;

            case POST_MESSAGE_IN_TYPES.POST_FEATURE_DATA:
                console.log('Feature ' + event.data.id + ' posted some data: ', event.data.featureData);
                // updateFeatureVars(event.data.id, event.data.featureData);
                
                // Now that we can preview chains and switch the previewing feature on the fly, we need to save a copy of any passed in data so we can access it when we shithc features
                if (event.data.id !== 'previewFrame') {
                    const mergedData = allFeaturesPassedInPreviewData_ref.current[event.data.id] || {};
                    for (let prop in event.data.featureData) {
                        if (typeof mergedData[prop] === 'undefined' && typeof props.featureVars.current[event.data.id][prop] !== 'undefined') {
                            mergedData[prop] = JSON.parse(JSON.stringify(props.featureVars.current[event.data.id][prop]));
                        }
                    }
                    allFeaturesPassedInPreviewData_ref.current[event.data.id] = deepObjectMerge({...mergedData}, event.data.featureData);
                    setAllFeaturesPassedInPreviewData(allFeaturesPassedInPreviewData_ref.current);
                }

                if (event.data.id === previewControlsId_ref.current) {
                    console.log('Merge with passed in data: ', passedInPreviewData);

                    // Go through the data being passed in and make sure we have the original versions in our passedInPreviewData,
                    // ready for merging with the updated data
                    for (let prop in event.data.featureData) {
                        if (typeof passedInPreviewData[prop] === 'undefined' && typeof props.featureVars.current[props.currentFeature._id][prop] !== 'undefined') {
                            passedInPreviewData[prop] = JSON.parse(JSON.stringify(props.featureVars.current[props.currentFeature._id][prop]));
                        }
                    }

                    // Preview Frame has returned some data back. Lets remember it, but not save it...
                    // {...passedInPreviewData, ...event.data.featureData};
                    const newPreviewData = deepObjectMerge({...passedInPreviewData}, event.data.featureData);
                    setPassedInPreviewData(newPreviewData);
                }
                break;

            case POST_MESSAGE_IN_TYPES.PASS_DATA_TO_DISRUPTOR:
                console.log('Feature ' + event.data.id + ' passing data another feature: ', event.data.featureId, event.data.data);
                if (event.data.data) {
                    const dataToSend = {
                        fromFeatureId: event.data.id,
                        data: event.data.data
                    }
                    postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.PASS_DATA_TO_DISRUPTOR, dataToSend, event.data.featureId);
                }
                break;

            default:
                break;
        }
    }

    const setUpPostMessageListener = () => {
        if (!window.listenerFunc) {
            window.listenerFunc = processPostedMessages;
            window.addEventListener(
                "message",
                window.listenerFunc,
                true
            );
        }
    }

    const closePreview = (e) => {
        console.log('Removing message listener');
        window.removeEventListener(
            "message",
            window.listenerFunc,
            true
        );
        window.listenerFunc = null;
        // Reset any feature vars that need to be reset on load, so when we load the actual feature the default settings is preserved
        resetOnLoadDefaultFeatureVars();
        props.closeFunc(e);
    }

    const goFullScreenPreview = () => {
        const elem = document.getElementById('previewContent');
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        } else if (elem.mozRequestFullScreen) { /* Firefox */
            elem.mozRequestFullScreen();
        } else if (elem.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
            elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE/Edge */
            elem.msRequestFullscreen();
        }
    }

    const resetOnLoadDefaultFeatureVars = () => {
        const featureVars = props.featureVars;
        const featureData = props.currentFeature;
        // Any feature vars that are configured in featureData to resetDefaultOnLoad, we need to reset to the default value
        // console.log('[resetOnLoadDefaultFeatureVars] Feature vars: ', featureVars, featureData);
        let madeChanges = false;
        for (let i = 0; i < featureData.feature.defaultVars.length; i++) {
            let varGroupConfig = featureData.feature.defaultVars[i];
            for (let j = 0; j < varGroupConfig.variables.length; j++) {
                let varConfig = varGroupConfig.variables[j];
                // console.log('Checking feature var: ', varGroupConfig, varConfig, featureVars.current[featureData._id][varGroupConfig.varKey][varConfig.varName], varConfig.defaultVal);
                if (varConfig.resetDefaultOnLoad) {
                    // console.log('Needs to be reset: ', varConfig.varName);
                    
                    if (typeof(varConfig?.useCustomisationValueOnLoad) === 'string' && varConfig.useCustomisationValueOnLoad !== '') {
                        featureVars.current[featureData._id][varGroupConfig.varKey][varConfig.varName] = getValFromObject(props.customisationVars?.current ?? props.customisationVars, props.currentFeature._id, varConfig?.useCustomisationValueOnLoad);
                        // console.log('Set live value to customisation value: ', getValFromObject(props.customisationVars?.current ?? props.customisationVars, props.currentFeature._id, varConfig?.useCustomisationValueOnLoad), featureVars.current[featureData._id][varGroupConfig.varKey][varConfig.varName]);
                    } else
                    if (featureVars.current[featureData._id][varGroupConfig.varKey][varConfig.varName] !== varConfig.defaultVal) {
                        featureVars.current[featureData._id][varGroupConfig.varKey][varConfig.varName] = varConfig.defaultVal;
                        madeChanges = true;
                    }
                }
            }
        }

        // Add in our base urls so our features can access them from the state in bootstrap.js
        let baseUrls = {
            frontend: frontendBaseUrl,
            backend: backendBaseUrl,
        }
        for (let p in featureVars.current) {
            if (typeof featureVars.current[p] === 'object') {
                featureVars.current[p].baseUrls = baseUrls;
                if (typeof featureVars.current[p].theme === 'undefined') {
                    featureVars.current[p].theme = {id: ''};
                }
            }
        }

        if (madeChanges) {
            props._reportFeatureVarsChanged();
        }
    }

    useEffect(
        () => {
            setUpPostMessageListener();
            resetOnLoadDefaultFeatureVars();
            getThemeData();
        }, []
    )

    const findPrimaryCommand = () => {
        let command = null;
        for (let i = 0; i < props.currentFeature.feature.commands.length; i++) {
            if (props.currentFeature.feature.commands[i].isPrimaryTrigger) {
                command = props.currentFeature.feature.commands[i].command;
                break;
            }
        }
        return command;
    }

    const sendPrimaryCommand = (e, forceStart = false) => {
        if (previewLoaded || forceStart) {
            let command = findPrimaryCommand();
            if (command) {
                props._sendFeatureCommandFunc(e, command, props.currentFeature._id, 0);
            }
        }
    }

    const sendQuickCommand = (e, command, param) => {
        if (previewLoaded) {
            props._sendFeatureCommandFunc(e, command, props.currentFeature._id, param || 0);
        }
    }

    const previewRefreshTimeoutId = useRef(null);
    const refreshPreview = (force = false) => {
        if (canBeRefreshed || force) {
            setPreviewLoaded(false);
            framesLoaded.current[0] = false;
            setNeedsRefresh(false);
            clearTimeout(previewRefreshTimeoutId.current);
            previewRefreshTimeoutId.current = setTimeout(
                () => {
                    setCanBeRefreshed(false);
                    const frame = "previewFrame";
                    const frameRef = document.getElementById(frame);
                    frameRef.contentWindow.location.reload();
                },
                3000
            );
        } else {
            setNeedsRefresh(true);
        }
    }
    useEffect(
        refreshPreview,
        [props.previewUpdate]
    )

    const [showDiagnotics, setShowDiagnostics] = useState(true);
    const closeDiagnostics = () => {
        setShowDiagnostics(false);
    }

    const [diagnosticsCollapsed, setDiagnosticsCollapsed] = useState(true);
    const toggleDiagnostics = () => {
        setDiagnosticsCollapsed((oldVal) => !oldVal);
    }

    console.log('Feature preview: ', props.currentFeature);

    /* Chain support */
    // CHAINS - used for intros / outros / feature countdowns!

    // const isChainMaster = useRef(false);
    const chainObjects_ref = useRef({});
    const [chainObjects, setChainObjects] = useState(null);
    const activeChain_ref = useRef(null);
    const [activeChain, setActiveChain] = useState(null);
    const chainTimeouts_ref = useRef([]);
    const clearChainTimeouts = () => {
        for (let i = 0; i < chainTimeouts_ref.current.length; i++) {
            clearTimeout(chainTimeouts_ref.current[i]);
        }
        chainTimeouts_ref.current = [];
    }
    useEffect(
        () => {
            return () => {
                clearChainTimeouts();
            }
        },
        []
    );
    const setChainItemVal = (chainId, itemIndex, key, val) => {
        chainObjects_ref.current[chainId].chainItems[itemIndex][key] = val;
        setChainObjects({ ...chainObjects_ref.current });
        /* if (chatConnected) {
            console.log('Emitting chain update!');
            socket.current.emit('chainUpdateUp', { streamID: streamId, chains: chainObjects_ref.current });
        } */
    }
    const startChain = (chainId) => {
        /* if (chatConnected && !chainObjects_ref.current[chainId].active) {
            socket.current.emit('chainStart', { streamID: streamId, chainId: chainId });
        } */
        if (activeChain === null) {
            const activatingChain = chainObjects[chainId];
            activatingChain.active = true;
            let chainIndex = -1;
            for (let i = 0; i < activatingChain.chainItems.length; i++) {
                if (activatingChain.chainItems[i].enabled) {
                    chainIndex = i;
                    break;
                }
            }
            if (chainIndex >= 0) {
                activatingChain.active = true;
                activatingChain.index = chainIndex;
                activeChain_ref.current = activatingChain;
                setActiveChain(activatingChain);
                if (activatingChain.chainItems[chainIndex].startType === CHAINS_HELPER.START_TYPE_AUTO) {
                    if (activatingChain.chainItems[chainIndex].startDelay > 0) {
                        chainTimeouts_ref.current.push(
                            setTimeout(() => {
                                sendChainCommand(activatingChain, chainIndex);
                            }, activatingChain.chainItems[chainIndex].startDelay * 1000)
                        );
                    } else {
                        sendChainCommand(activatingChain, chainIndex);
                    }
                }
                if (activatingChain.chainItems[chainIndex].endType === CHAINS_HELPER.END_TYPE_IMMEDIATE) {
                    chainStep(activatingChain.chainItems[chainIndex].featureId);
                }
            }
        }
    }

    const sendChainCommand = (chainData, chainItemIndex) => {
        const featureId = chainData.chainItems[chainItemIndex].featureId;
        const command = chainData.chainItems[chainItemIndex].command;
        const commandParam = chainData.chainItems[chainItemIndex].commandParam ?? 0;
        sendFeatureCommandFunc(null, command, featureId, commandParam, featureId);
        selectDisruptor(featureId);
        setChainSkipAllowed(true);
        setForceSkipAllowed(false);
    }
    const chainStep = (featureEndedId, looped = false) => {
        if (activeChain_ref.current) {
            const chainData = activeChain_ref.current;
            let chainItemIndex = chainData.index;
            if (chainData.chainItems[chainItemIndex].featureId === featureEndedId || looped) {
                if (!looped || !chainData.chainItems[chainItemIndex].enabled) {
                    for (let i = chainItemIndex + 1; i < chainData.chainItems.length; i++) {
                        if (i < chainData.chainItems.length && chainData.chainItems[i].enabled) {
                            chainItemIndex = i;
                            break;
                        }
                    }
                }
                if (chainItemIndex > chainData.index || looped) {
                    chainData.index = chainItemIndex;
                    // sendChainCommand(chainData, chainItemIndex);
                    if (chainData.chainItems[chainItemIndex].startType === CHAINS_HELPER.START_TYPE_AUTO) {
                        if (chainData.chainItems[chainItemIndex].startDelay > 0) {
                            chainTimeouts_ref.current.push(
                                setTimeout(() => {
                                    sendChainCommand(chainData, chainItemIndex);
                                }, chainData.chainItems[chainItemIndex].startDelay * 1000)
                            );
                        } else {
                            sendChainCommand(chainData, chainItemIndex);
                        }
                    }
                    if (chainData.chainItems[chainItemIndex].endType === CHAINS_HELPER.END_TYPE_IMMEDIATE) {
                        chainStep(chainData.chainItems[chainItemIndex].featureId);
                    }
                } else {
                    // No more items in this chain - stop it (or loop it)
                    if (chainData.chainItems[chainItemIndex].loop) {
                        setLoopingChain(true);
                        // setActiveChain(null);
                        chainTimeouts_ref.current.push(
                            setTimeout(() => {
                                setLoopingChain(false);
                                chainData.index = 0;
                                // startChain(chainData.id);
                                chainStep(null, true);
                            }, chainData.chainItems[chainItemIndex].loopDelay * 1000 + 10)
                        );
                    } else {
                        stopChain(chainData.id);
                    }
                }
            }
        }
    }

    const stopChain = (chainId) => {
        /* if (chatConnected && chainObjects_ref.current[chainId].active) {
            socket.current.emit('chainStop', { streamID: streamId, chainId: chainId });
        } */
        const chainData = chainObjects_ref.current[chainId];
        chainData.active = false;
        chainData.index = 0;
        activeChain_ref.current = null;
        setActiveChain(null);
        clearChainTimeouts();
        setLoopingChain(false);
    }

    const [chainSkipAllowed, setChainSkipAllowed] = useState(true);
    const [forceSkipAllowed, setForceSkipAllowed] = useState(false);
    const [loopingChain, setLoopingChain] = useState(false);
    const skipChainItem = (chainId) => {
        // console.log('Skipping chain item: ', chainId, activeChain);
        if (activeChain.id === chainId) {
            if (chainSkipAllowed) {
                if (featureId === null) {
                    setFeatureId(activeChain.chainItems[activeChain.index].featureId);
                }
                setChainSkipAllowed(false);
                setForceSkipAllowed(false);
                setTimeout(() => {
                    setForceSkipAllowed(true);
                }, 1000);
                endFeature(featureId);
            } else
            if (forceSkipAllowed) {
                chainStep(activeChain.chainItems[activeChain.index].featureId);
                setForceSkipAllowed(false);
            }
        }
        // Fail-safe in case features have a bug where they don't send the feature ended message
        // Give it a second then first a socket message to advance the chain.
        // The server will respoond to this message, advancing the chain but only if the itemIndex we send is the current index.
        //setChainSkipAllowed(false);
        /* setTimeout(() => {
            socket.current.emit('chainSkipItem', { streamID: streamId, chainId: chainId, chainItemIndex: chainItemIndex });
            setChainSkipAllowed(true);
        }, 5000); */
    }

    const endFeature = (triggerEndFeatureId) => {
        // Find the correct command
        const campaignData = /* campaignData_ref.current || */ campaignContext.campaignData;
        let featureData_end = null;
        if (campaignData && campaignData.features) {
            for (let i = 0; i < campaignData.features.length; i++) {
                if (campaignData.features[i]._id === triggerEndFeatureId) {
                    featureData_end = campaignData.features[i];
                }
            }
            if (featureData_end) {
                console.log('Find end command: ', featureData_end);
                let commandString_end = '';
                for (let i = 0; i < featureData_end.feature.commands.length; i++) {
                    if (featureData_end.feature.commands[i].isEndTrigger) {
                        commandString_end = featureData_end.feature.commands[i].command;
                    }
                }
                if (commandString_end) {
                    console.log('Emitting end feature command: ', commandString_end, triggerEndFeatureId);
                    /* if (socket.current) {
                        const timestamp = Date.now();
                        // We are connected - emit a socket message so our triggered feature gets logged and all that (just like clicking the control button)
                        socket.current.emit('triggerFeatureEnd', { streamID: streamId, timestamp: timestamp, featureID: triggerEndFeatureId, triggerFeatureID: triggerEndFeatureId, command: commandString_end });
                    } */
                }
            }
        }
    }

    const requestChainDataFromServer = () => {
        // We send up an empty object, which is a request for the server's (source of truth) chain states.
        // socket.current.emit('chainUpdateUp', { streamID: streamId, chains: {} });
    }

    const requestLiveStreamStateFromServer = () => {
        // socket.current.emit('requestLiveStreamState', { streamID: streamId });
    }

    const buildAllFeatureChains = (receivedCampaignChainData = null) => {
        // Weirdly can't seem to access campaignContext from this function after a refresh,
        // even though it works fine elsewhere...

        // isChainMaster is set on the client where the user clicked the connect button,
        // meaning only one dashboard instance will build and emit the chains.
        /* if (!isChainMaster.current) {
            return;
        } */

        const findChainFromFlowData = (flowId, flows) => {
            let flowIndex = -1;
            for (let i = 0; i < flows.length; i++) {
                // console.log('Checking flow: ', flows[i], flowId);
                if (flows[i].id === flowId) {
                    flowIndex = i;
                    break;
                }
            }
            if (flowIndex >= 0) {
                // console.log('Found chain from flow data: ', campaignContext.campaignData.chains[flowIndex]);
                return campaignContext.campaignData.chains[flowIndex];
            }
            return null;
        }

        const passedInChainId = (props.showChains && props.flows && props.activeFlowId) ? deriveChainId(campaignContext.campaignData.chains.indexOf(findChainFromFlowData(props.activeFlowId, props.flows)), campaignContext.campaignData) : (props.showChains && props.featureChain) ? deriveChainId(campaignContext.campaignData.chains.indexOf(props.featureChain), campaignContext.campaignData) : null;

        // console.log('Building chains for chainId: ', passedInChainId, findChainFromFlowData(props.activeFlowId, props.flows));

        const campaignData = /* campaignData_ref.current || */ campaignContext.campaignData;
        console.log('CAMPAIGN DATA ===============', campaignData, props.activeFlowId, props.flows)
        let gotChains = false;
        if (campaignData && campaignData.features && campaignData.features.length > 0 && campaignData.chains && campaignData.chains.length > 0) {
            for (let i = 0; i < campaignData.chains.length; i++) {
                if (campaignData.chains[i].length === 0) {
                    continue;
                }
                const chainId = deriveChainId(i, campaignData);
                if (chainId === passedInChainId) {
                    const builtChain = buildFeatureChainStateObject(chainId, campaignData, receivedCampaignChainData);
                    if (builtChain.chainItems && builtChain.chainItems.length > 0) {
                        gotChains = true;
                        console.log('Got chains!', builtChain, chainId, campaignData.chains[i]);
                        builtChain.chainItems[builtChain.chainItems.length - 1].loop = false;
                        builtChain.chainItems[builtChain.chainItems.length - 1].loopDelay = 0;
                    }
                }
            }
            /* for (let i = 0; i < campaignData.features.length; i++) {
                if (campaignData.features[i].feature.featureType === 'feature') {
                    const builtChain = buildFeatureChainStateObject(campaignData.features[i]._id, campaignData, receivedCampaignChainData);
                    if (builtChain.chainItems && builtChain.chainItems.length > 0) {
                        gotChains = true;
                    }
                }
            } */
        } // else {
        // Campaign data not avilable...
        // Try again in a moment...
        // setTimeout(buildAllFeatureChainsAndSendToServer, 1000);
        // }

        if (gotChains) {
            // console.log('UP DATA', streamId, chainObjects_ref.current)
            // socket.current.emit('chainUpdateUp', { streamID: streamId, chains: chainObjects_ref.current });
            console.log('Got chains!', chainObjects_ref.current);
        }

        // isChainMaster.current = false;
    }

    const buildFeatureChainStateObject = (chainId, _campaignData = null, receivedCampaignChainData = null) => {
        const campaignData = _campaignData || campaignContext.campaignData;
        // First things first - if we have some received campaign chain data, use that!
        // The server is our ultimate source of truth, but only needs to provide the basic chain data, we can build the chain objects here.
        // if (receivedCampaignChainData) {
            console.log('Updating campaign chain data: ', receivedCampaignChainData);
            // campaignData.chains = receivedCampaignChainData;
            // if (isChainMaster.current) {
                // chainObjects_ref.current = {};
            // }
       //  }
        // console.log('Build chain obj: ', featureId, campaignData);
        let rawChainData = null;
        /*
        for (let i = 0; i < campaignData.chains.length; i++) {
            if (campaignData.chains[i].indexOf(featureId) >= 0) {
                // our chain!
                rawChainData = campaignData.chains[i];
                break;
            }
        } */

        // Look for out raw chain data. To do this we need to generate the chainId, we do this as follows:
        // campaignData.chains is a basic array of arrays container feature IDs.
        // We step through the feature IDs in the chain and if one is a feature type then that is the Id we derive our chainId from,
        // otherwise we use the first feature ID in the chain.
        // Then we add and _ and thhe index of the chain in the campaignData.chains array.
        let derivedChainId = null;
        for (let i = 0; i < campaignData.chains.length; i++) {
            derivedChainId = deriveChainId(i, campaignData);
            if (chainId === derivedChainId) {
                // found our chainId
                rawChainData = campaignData.chains[i];
                break;
            }
            /* if (campaignData.chains[i].indexOf(featureId) >= 0) {
                // our chain!
                rawChainData = campaignData.chains[i];
                break;
            } */
        }

        if (!rawChainData) {
            // No chain data found return an empty object
            console.log('No chain data found for chainId: ', chainId);
            return {};
        }

        // The feature associated with the chain id - used to label our chain
        let chainIdFeatureData = findFeatureData(chainId.split('_')[0]);

        // console.log('Chain data (raw): ', rawChainData, featureData);
        let builtChainData = null;
        if (chainObjects_ref.current[chainId]) {
            // fetch it (this stuff is persistent)!
            console.log('Using persitent chain data: ', chainObjects_ref.current[chainId]);
            builtChainData = chainObjects_ref.current[chainId].chainItems;
        } else {
            // build it
            builtChainData = [];

            // now go through our feature chain, intro, feature, outro...
            for (let i = 0; i < rawChainData.length; i++) {
                const parsedChainItem = CHAINS_HELPER.parseChainItem(rawChainData[i]);
                const chainStepData = {};
                chainStepData.enabled = true;
                chainStepData.featureId = parsedChainItem.featureId;
                chainStepData.startType = parsedChainItem.startType;
                chainStepData.startDelay = parsedChainItem.startDelay;
                chainStepData.endType = parsedChainItem.endType;
                // Find the command!
                for (let j = 0; j < campaignData.features.length; j++) {
                    if (campaignData.features[j]._id === parsedChainItem.featureId) {
                        // found the feature...
                        for (let k = 0; k < campaignData.features[j].feature.commands.length; k++) {
                            if (campaignData.features[j].feature.commands[k].isPrimaryTrigger) {
                                // Found our trigger command
                                chainStepData.command = campaignData.features[j].feature.commands[k].command;
                                break;
                            }
                        }
                        if (chainStepData.command === undefined) {
                            // We didn't find a trigger command...
                            chainStepData.command = campaignData.features[j].feature.commands[0].command;
                            break;
                        }
                    }
                }

                builtChainData.push(chainStepData);
            }

            chainObjects_ref.current[chainId] = { id: chainId, active: false, index: 0, label: chainIdFeatureData.contentLabel + ` (${builtChainData.length})`, chainItems: builtChainData };
            setChainObjects({ ...chainObjects_ref.current });
        }
        console.log('Built chains: ', chainObjects_ref.current, builtChainData);
        return chainObjects_ref.current && chainObjects_ref.current[chainId] ? chainObjects_ref.current[chainId] : {};
    }

    const selectDisruptor = (featureId) => {
        const featureData = findFeatureData(featureId);
        console.log('Selected disruptor: ', featureData, featureId, allFeaturesPassedInPreviewData_ref.current);
        setFeature(featureData);
        setControlsLabel(featureData.contentLabel || featureData.feature.featureName);
        previewControlsId_ref.current = featureId;
        setPreviewControlsId(featureId);
        setFeatureId(featureId);
        setPassedInPreviewData(allFeaturesPassedInPreviewData_ref.current[featureId] || {});
    }

    const getChainedDisruptorPanel = (item, featureData, i, chainId = null) => {
        console.log('Get disruptor panel for: ', item, featureData);

        let boxStyle = featureData.feature.featureType;
        if (LEFT_COLOR_BLOCK_STYLES[featureData.feature.featureType + '_' + featureData.feature.varKey]) {
            boxStyle = featureData.feature.featureType + '_' + featureData.feature.varKey;
        }
        if (LEFT_COLOR_BLOCK_STYLES['disruptor_' + featureData.feature.colourCategory]) {
            boxStyle = 'disruptor_' + featureData.feature.colourCategory;
        }
        if (LEFT_COLOR_BLOCK_STYLES['category_' + featureData.feature.colourCategory]) {
            boxStyle = 'category_' + featureData.feature.colourCategory;
        }
        
        return (
            <div className="fl-column compact" key={'disruptor_' + i}>
                <div className="fl-row grow">
                    <LeftColorBlockBox style={boxStyle} overrideBgColor={feature === featureData ? '#DDDDEE' : ''}>
                        <div className="fl-row list-view-content">

                            <div className="grow">
                                {featureData.contentLabel || `Disruptor Name #${i + 1}`}
                                <br />
                                <span className={`bot-time-dark`}>
                                    ({featureData.feature.featureName})
                                </span>
                                {item.startType === CHAINS_HELPER.START_TYPE_MANUAL &&
                                    <>
                                        <br />
                                        <span className={`small-text warning-text`}>
                                            REQUIRES MANUAL START
                                        </span>
                                    </>
                                }
                                {item.startType === CHAINS_HELPER.START_TYPE_AUTO && item.startDelay > 0 &&
                                    <>
                                        <br />
                                        <span className={`small-text warning-text`}>
                                            +{item.startDelay} SECOND DELAY
                                        </span>
                                    </>
                                }
                            </div>

                            <div className="fl-row shrink">
                                {previewLoaded &&
                                    <Toggle
                                        id={'fc_tog_' + i}
                                        currentState={item.enabled}
                                        toggleFunc={(e) => {setChainItemVal(chainId, i, 'enabled', !item.enabled)}}
                                    />
                                }

                                <div className="edit-icon" onClick={(e) => selectDisruptor(featureData._id)}>
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.controls, IconJsxer.ICON_STYLES.campaignPanelTop)}
                                </div>
                            </div>

                        </div>
                    </LeftColorBlockBox>
                </div>
            </div>
        )
    }

    useEffect(
        () => {
            if (props.showChains) {
                buildAllFeatureChains();
            }
        }, []
    )

    const sendFeatureCommandFunc = (e, commandString, featureId, param = null, forcePreviewControlsId = null) => {
        props._sendFeatureCommandFunc(e, commandString, featureId, param, forcePreviewControlsId || previewControlsId);
    }


    let chainItemNumber = 0;
    let showChainItemNumber = true;
    let showNextChainItemNumber = true;

    const collapsedFrameStyle = previewLoaded ? {} : {height: 0, overflow: 'hidden', visibility: 'hidden'};
    return (
        <div className={`preview-mode-frame-holder${maximized ? ' preview-maximized' : ''}`} style={maximized ? {} : { left: previewX, top: previewY }} onDragStart={!maximized ? dragStartHandler : null} draggable={!maximized}>
            <HeadedPanel
                color={previewLoaded ? 'primary' : 'error'}
                className={maximized ? 'preview-maximized' : ''}
                headingContent={
                    <div className="fl-row preview-title-holder">
                        <div className="preview-title">
                            {`${previewLoaded ? (props.currentFeature.contentLabel || props.currentFeature.feature.featureName) + ' ' : ''}Preview${previewLoaded ? '' : ' Loading'}${props.titlePostFix !== '' ? ' ' + props.titlePostFix : ''}`}
                        </div>
                        <div className="grow"></div>

                        {!maximized && props.showQuickPlayButton &&
                            <>
                                <div className="win-controls" onClick={sendPrimaryCommand}>
                                    {IconJsxer.GetIcon(
                                        IconJsxer.ICONS.play,
                                        IconJsxer.ICON_STYLES.campaignPanelTop,
                                    )}
                                </div>
                                {props.currentFeature.feature.commands.map(
                                    (val, i, arr) => {
                                        if (val.previewQuickCommand && val.previewQuickIcon && IconJsxer.ICONS[val.previewQuickIcon] && val.command !== findPrimaryCommand()) {
                                            return (
                                                <div className="win-controls" onClick={(e) => sendQuickCommand(e, val.command, val.previewQuickParam || 0)} key={'wc_' + i}>
                                                    {IconJsxer.GetIcon(
                                                        IconJsxer.ICONS[val.previewQuickIcon],
                                                        IconJsxer.ICON_STYLES.campaignPanelTop,
                                                    )}
                                                </div>
                                            )
                                        } else {
                                            return null
                                        }
                                    }
                                )}
                            </>
                        }
                        {maximized &&
                            <div className="win-controls" onClick={goFullScreenPreview}>
                                {IconJsxer.GetIcon(
                                    IconJsxer.ICONS.magnify,
                                    IconJsxer.ICON_STYLES.campaignPanelTop,
                                )}
                            </div>
                        }
                        {!maximized && props.resizeable &&
                            <div className="win-controls" onClick={() => setMaximized(true)}>
                                {IconJsxer.GetIcon(
                                    IconJsxer.ICONS.winMaximize,
                                    IconJsxer.ICON_STYLES.campaignPanelTop,
                                )}
                            </div>
                        }
                        {maximized && props.resizeable &&
                            <div className="win-controls" onClick={() => setMaximized(false)}>
                                {IconJsxer.GetIcon(
                                    IconJsxer.ICONS.winRestore,
                                    IconJsxer.ICON_STYLES.campaignPanelTop,
                                )}
                            </div>
                        }
                        {props.closeFunc &&
                            <div className="win-controls" onClick={closePreview}>
                                {IconJsxer.GetIcon(
                                    IconJsxer.ICONS.closeX,
                                    IconJsxer.ICON_STYLES.campaignPanelTop,
                                )}
                            </div>
                        }
                    </div>
                }
            >
                <div className="preview-frame-holder">
                    <div className="grow fl-start">
                        <div id="previewContent" className={`preview-mode-frame${maximized ? ' frame-maximized' : ''}${randomPreviewBG>.5 ? ' preview-random-1':' preview-random-2'}`}>
                            {showDiagnotics && (cancelledFrames.length > 0 || framesLoaded.current.indexOf(false) >= 0 || authContext.userData.userLevel >= ACCOUNT_TYPES.koko.level) &&
                                <div className={`preview-diagnostics${diagnosticsCollapsed ? ' diagnostics-collapsed' : ''}`}>
                                    <div className="fl-row grow compact">
                                        <h3 onClick={toggleDiagnostics}>LOADER DIAGNOSTICS{isDevMode() ? ' (DEV)' : ''}</h3>
                                        <div className="grow"></div>
                                        <div className="shrink" onClick={closeDiagnostics}><h3 className="close-diagnotics-button">X</h3></div>
                                    </div>
                                    {!diagnosticsCollapsed && authContext.userData.userLevel >= ACCOUNT_TYPES.koko.level &&
                                        <div className="small-text">
                                            Will stick for Koko Admins to allow output of feature data.
                                        </div>
                                    }
                                    {!diagnosticsCollapsed && framesLoaded.current.map(
                                        (val, i, arr) => {
                                            return (
                                                <div key={'fr_' + i} className="diagnostic-holder">
                                                    <div>{i === 0 ? 'Main Preview' : campaignContext.campaignData.features[i - 1].contentLabel}<br /><span className={`${cancelledFrames.indexOf(frameIds.current[i]) >= 0 ? 'cancelled' : val ? 'loaded' : 'loading'}`}>{cancelledFrames.indexOf(frameIds.current[i]) >= 0 ? 'Cancelled' : val ? 'Loaded' : 'Loading'}</span></div>
                                                    <div className="grow"></div>
                                                    {!val && cancelledFrames.indexOf(frameIds.current[i]) < 0 &&
                                                        <div className="shrink">
                                                            <button className="standard-button slim-button" onClick={() => cancelFrame(frameIds.current[i])}>Cancel</button>
                                                        </div>
                                                    }
                                                    {authContext.userData.userLevel >= ACCOUNT_TYPES.koko.level &&
                                                        <div className="shrink">
                                                            <button className="standard-button slim-button" onClick={() => logoutData(frameIds.current[i])}>Data</button>
                                                        </div>
                                                    }
                                                </div>
                                            )
                                        }
                                    )}    
                                </div>
                            }

                            {props.currentFeature &&
                                <iframe title="previewFrame" id="previewFrame" name="previewFrame" style={{...collapsedFrameStyle, zIndex: 450 + (findFeature(props.currentFeature._id, campaignContext.campaignData).stackingOrder || 999)}} src={isDevMode() ? PREVIEW_DEV_URL : props.currentFeature.feature.featureURL + "?demoMode=true"} />
                            }
                            {// findLinkedFeatureTriggers(props.currentFeature._id, campaignContext.campaignData, props.customisationVars.current).map(
                            campaignContext.campaignData.features.map(
                                (val, i, arr) => {
                                    if (framesLoaded.current.length <= i + 1) {
                                        framesLoaded.current.push(false);
                                        frameIds.current.push(val._id);
                                        fontsToLoad.current.push(customFonts?.length ?? 0);
                                    }
                                    linkedFeatureData.current[val._id] = val;
                                    if (cancelledFrames.indexOf(val._id) < 0) {
                                        return (
                                            <iframe key={val._id} title={val._id} id={val._id} name={val._id} style={{...collapsedFrameStyle, zIndex: 450 + (findFeature(val._id, campaignContext.campaignData).stackingOrder || 999)}} src={val.feature.featureURL + "?demoMode=true"} />
                                        )
                                    } else {
                                        return null;
                                    }
                                }
                            )}
                            {!previewLoaded &&
                                <div className={`preview-loading${maximized ? ' frame-maximized' : ''}${randomPreviewBG>.5 ? ' preview-random-1':' preview-random-2'}`}>
                                    <img className="glitch" alt="Twitch Glitch 1" src={assets.LoadingFeature} />
                                </div>
                            }
                        </div>
                    </div>

                    {maximized && previewLoaded &&
                    <div className="fl-column shrink fl-start controls-area form-holder">

                        {props.showChains && 
                            <>
                                <h3>Feature Chain</h3>
                                {campaignContext.campaignData.chains.map(
                                    (chainVal, chainIndex, arr) => {
                                        const chainId = deriveChainId(chainIndex, campaignContext.campaignData);
                                        if (chainId === null) {
                                            return null;
                                        }
                                        const featureChainStateObject = chainObjects[chainId]; // buildFeatureChainStateObject(chainId);
                                        /* if (val.feature.featureType === 'feature') {
                                            const featureChainStateObject = buildFeatureChainStateObject(val._id);
                                            console.log('Feature chain state: ', featureChainStateObject);

                                            // Check tabs in featureData for any themeable tabs
                                            const featureData = val;
                                            let themeable = false;
                                            for (let i = 0; i < featureData.feature.customisation.length; i++) {
                                                if (featureData.feature.customisation[i].themeable) {
                                                    themeable = true;
                                                    break;
                                                }
                                            } */

                                        // Find the feature data the chainId was drived from, so we can name ir properly.
                                        const chainIdFeatureData = findFeatureData(chainId.split('_')[0]);
                                        if (!chainIdFeatureData || !featureChainStateObject) {
                                            return null;
                                        }
                                        return (
                                            <CollapsibleHeadedSection
                                                key={'chain_' + chainId}
                                                extraHeaderContentLeft={
                                                    <>
                                                        {!previewLoaded &&
                                                            <>
                                                                {<div className="edit-icon disabled" onClick={(e) => e.stopPropagation()}>
                                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.play, IconJsxer.ICON_STYLES.campaignPanelTop)}
                                                                </div>}
                                                            </>
                                                        }
                                                        {previewLoaded && !featureChainStateObject.active && activeChain === null &&
                                                            <div className="edit-icon" onClick={(e) => { e.stopPropagation(); startChain(chainId) }}>
                                                                {IconJsxer.GetIcon(IconJsxer.ICONS.play, IconJsxer.ICON_STYLES.campaignPanelTop)}
                                                            </div>
                                                        }
                                                        {previewLoaded && featureChainStateObject.active && activeChain === featureChainStateObject &&
                                                            <>
                                                                <div className="edit-icon" onClick={(e) => { e.stopPropagation(); stopChain(chainId) }}>
                                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.stop, IconJsxer.ICON_STYLES.campaignPanelTop)}
                                                                </div>
                                                                {loopingChain &&
                                                                    <>
                                                                        <div className='edit-icon disabled' onClick={(e) => { e.stopPropagation(); }}>
                                                                            {IconJsxer.GetIcon(IconJsxer.ICONS.loop, IconJsxer.ICON_STYLES.campaignPanelTop)}
                                                                        </div>
                                                                        <div className="small-text light-text">(Looping...)</div>
                                                                    </>
                                                                }
                                                                {!loopingChain &&
                                                                    <>
                                                                        <div className={`edit-icon${(!chainSkipAllowed && !forceSkipAllowed) ? ' disabled' : ''}`} onClick={(chainSkipAllowed || forceSkipAllowed) ? (e) => { e.stopPropagation(); skipChainItem(chainId)} : (e) => { e.stopPropagation(); }}>
                                                                            {IconJsxer.GetIcon(IconJsxer.ICONS.skip, IconJsxer.ICON_STYLES.campaignPanelTop)}
                                                                        </div>
                                                                        {!chainSkipAllowed && !forceSkipAllowed &&
                                                                            <div className="small-text light-text">(Moving on...)</div>
                                                                        }
                                                                        {!chainSkipAllowed && forceSkipAllowed &&
                                                                            <div className="small-text light-text">(Click to force next step)</div>
                                                                        }
                                                                    </>
                                                                }
                                                            </>
                                                        }
                                                    </>
                                                }
                                                preHeaderContent={
                                                    <div className="group-icon">
                                                        {IconJsxer.GetIcon(IconJsxer.ICONS.customisationGroup, IconJsxer.ICON_STYLES.standard)}
                                                    </div>
                                                }
                                                title={`#${campaignContext.campaignData.chains.indexOf(props.featureChain) + 1} ${chainIdFeatureData.contentLabel} (${chainVal.length} steps)`}
                                                open={true}
                                            >

                                                <div className="fl-column compact standard-rem-top-bot-margin">
                                                    {/* FEATURE CHAIN CONTENT */}
                                                    {featureChainStateObject && featureChainStateObject.chainItems.length >= 1 && featureChainStateObject.chainItems.map(
                                                        (item, index, arr) => {
                                                            const featureData = findFeatureData(item.featureId);

                                                            showChainItemNumber = showNextChainItemNumber;
                                                            if (showChainItemNumber) {
                                                                chainItemNumber++;
                                                            }
                                                            if (item.endType === CHAINS_HELPER.END_TYPE_FEATURE_END) {
                                                                showNextChainItemNumber = true;
                                                            } else if (item.endType === CHAINS_HELPER.END_TYPE_IMMEDIATE) {
                                                                showNextChainItemNumber = false;
                                                            }
                                                            let activeItem = !loopingChain && featureChainStateObject.active && featureChainStateObject.index === index;
                                                            if (!activeItem && featureChainStateObject.active && item.endType === CHAINS_HELPER.END_TYPE_IMMEDIATE) {
                                                                // Check the next items in the chain until we get to one that is set to CHAIN_HELPER.END_TYPE_FEATURE_END
                                                                // If we find and active item, then this one is also active!
                                                                for (let i = index + 1; i < arr.length; i++) {
                                                                    if (featureChainStateObject.index === i) {
                                                                        activeItem = true;
                                                                        break;
                                                                    }
                                                                    if (arr[i].endType === CHAINS_HELPER.END_TYPE_FEATURE_END) {
                                                                        break;
                                                                    }
                                                                }
                                                            }
                                                            
                                                            if (featureData) {
                                                                return (
                                                                    <div className="fl-row" key={'chain_' + index}>
                                                                        {// featureChainStateObject.chainItems.length > 0 &&
                                                                            <div className="shrink">
                                                                                <div className={`feature-chain-position-holder${previewLoaded && activeItem ? ` ${chainSkipAllowed ? 'active' : 'skipping'}-chain-position` : !previewLoaded ? '' : (item.enabled ? ' enabled-chain-position' : '')}${showChainItemNumber ? '' : ' small-chain-position-holder'}`}>
                                                                                    {showChainItemNumber ? chainItemNumber : '+'}
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                        <div className="grow">
                                                                            {getChainedDisruptorPanel(item, featureData, index, chainId)}
                                                                        </div>
                                                                    </div>
                                                                )
                                                            } else {
                                                                return null;
                                                            }
                                                        }
                                                    )}

                                                    <div className="fl-row">
                                                        <div className="shrink">
                                                            <div className={`feature-chain-position-holder active-chain-position ${!previewLoaded ? '' : ` ${loopingChain ? 'active' : 'enabled'}-chain-position`} small-chain-position-holder`}>
                                                                {IconJsxer.GetIcon(IconJsxer.ICONS.loop, IconJsxer.ICON_STYLES.campaignPanelTop)}
                                                            </div>
                                                        </div>
                                                        <div className="fl-row grow">
                                                            <div className="form-field-content label">Loop Chain</div>
                                                                <div className="shrink">
                                                                    <Toggle
                                                                        id={"loopChain_" + chainId}
                                                                        label="Loop Chain"
                                                                        currentState={featureChainStateObject.chainItems[featureChainStateObject.chainItems.length - 1].loop}
                                                                        toggleFunc={
                                                                            (e, id) => {
                                                                                setChainItemVal(chainId, featureChainStateObject.chainItems.length - 1, 'loop', !featureChainStateObject.chainItems[featureChainStateObject.chainItems.length - 1].loop)
                                                                                // props._changeFeatureChainItemSetting(featureChain, i_f, CHAINS_HELPER.PARAM_INDEXES.START_TYPE, 'auto');
                                                                            }
                                                                        }
                                                                    />
                                                                </div>
                                                                {featureChainStateObject.chainItems[featureChainStateObject.chainItems.length - 1].loop &&
                                                                    <div className="shrink">
                                                                        <HMSTimeField
                                                                            id={"loopDelay_" + chainId}
                                                                            label="Loop Delay"
                                                                            value={featureChainStateObject.chainItems[featureChainStateObject.chainItems.length - 1].loopDelay}
                                                                            setFunc={
                                                                                (val) => {
                                                                                    setChainItemVal(chainId, featureChainStateObject.chainItems.length - 1, 'loopDelay', val)
                                                                                    // props._changeFeatureChainItemSetting(featureChain, i_f, CHAINS_HELPER.PARAM_INDEXES.START_DELAY, val);
                                                                                }
                                                                            }
                                                                            showHours={false}
                                                                            showError={false}
                                                                            inlineLabel={true}
                                                                        />
                                                                    </div>
                                                                }
                                                        </div>
                                                    </div>

                                                </div>
                                            </CollapsibleHeadedSection>
                                        )
                                    }
                                )}
                            </>
                        }

                        <FeatureControls
                            feature={feature}
                            streamVars={streamVars}
                            customisationVars={customisationVars}
                            featureId={featureId}
                            reportFeatureVarsChangedFunc={(onlyUpdateDisruptor) => props._reportFeatureVarsChanged(onlyUpdateDisruptor, previewControlsId)}
                            reportCustomisationVarsChanged={props._reportCustomisationVarsChanged}
                            sendFeatureCommandFunc={sendFeatureCommandFunc}
                            showTooltipFunc={props._showTooltip}
                            hideTooltipFunc={props._hideTooltip}
                            label={controlsLabel}
                            startCollapsed={!props.startControlsExpanded}
                            previewControls={true}
                            passedInPreviewData={passedInPreviewData}
                            quizData={quizData}
                            isThemeable={isThemeable}
                            themeData={themeData}
                            applyThemeFunc={() => refreshPreview(true)}
                        />
                        </div>
                    }
                </div>
            </HeadedPanel>

        </div>
    )
}

FeaturePreview.propTypes = {
    currentFeature: PropTypes.object.isRequired,
    featureVars: PropTypes.object.isRequired,
    customisationVars: PropTypes.object.isRequired,
    onLoad: PropTypes.func,
    closeFunc: PropTypes.func,

    startMaximized: PropTypes.bool,
    resizeable: PropTypes.bool,
    startControlsExpanded: PropTypes.bool,
    startPosX: PropTypes.string,
    startPosY: PropTypes.string,
    showQuickPlayButton: PropTypes.bool,
    autoStart: PropTypes.bool,

    previewUpdate: PropTypes.number,

    titlePostFix: PropTypes.string,

    _reportFeatureVarsChanged: PropTypes.func.isRequired,
    _reportCustomisationVarsChanged: PropTypes.func.isRequired,
    _sendFeatureCommandFunc: PropTypes.func.isRequired,
    _showTooltip: PropTypes.func,
    _hideTooltip: PropTypes.func,

    showChains: PropTypes.bool,
}

FeaturePreview.defaultProps = {
    startMaximized: false,
    resizeable: true,
    startControlsExpanded: false,
    titlePostFix: '',
    startPosX: 'left',
    startPosY: 'bottom',
    showQuickPlayButton: false,
    autoStart: false,
    previewUpdate: 0,
    showChains: false,
}

export default FeaturePreview;
